import { GameWithScoreByQtrID, IGameStats, SeasonID, SeasonWeekID } from '@shared/types';
import { teamMatchResultsConverter } from 'api/converters/TeamMatchResultsConverter';
import { Cls, Emojis } from 'app/constants';
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { breakSeasonWeekID } from 'helpers/IdHelpers';
import { getTeamStatsFromGameStats } from 'helpers/StatsHelper';
import { db } from 'lib/firebase';

/**
 * This module provides API functions for fetching and updating team match results.
 * It includes functions to fetch match results by season week ID
 * and to update team match results in the Firestore database.
 *
 * This was a fix to address the missing passingYardsAllowed and rushingYardsAllowed stats.
 *
 * @module AdminTeamResultsAPI
 */

/**
 * Fetch all game stats for a given seasonWeekID
 *
 * @param {SeasonWeekID} seasonWeekID - The season week ID to fetch match results for
 *
 * @return {Promise<IGameStats[]>} - A promise that resolves to an array of game stats
 */
const fetchMatchResultsBySeasonWeekID = async (seasonWeekID: SeasonWeekID): Promise<IGameStats[]> => {
  const { yearID } = breakSeasonWeekID(seasonWeekID);
  const collectionPath = `stats/${yearID}/matchresults`;
  const q = query(collection(db, collectionPath), where('seasonWeekID', '==', seasonWeekID));
  const snapshot = await getDocs(q);
  const matches: IGameStats[] = [];
  snapshot.forEach((doc) => {
    const match = doc.data() as IGameStats;
    matches.push(match);
  });
  return matches;
};

/**
 * Update team match results for a given seasonWeekID
 *
 * @param {SeasonWeekID} seasonWeekID - The season week ID to update match results for
 *
 * @return {Promise<void>} - A promise that resolves when the update is complete
 */
export const updateTeamMatchResults = async (seasonWeekID: SeasonWeekID): Promise<void> => {
  const { weekID, yearID } = breakSeasonWeekID(seasonWeekID);
  // fetch all games for given weekID
  const allGamesStats: IGameStats[] = await fetchMatchResultsBySeasonWeekID(seasonWeekID);
  const gameCount = allGamesStats.length;
  const msg = `${Emojis.eyes} %c LOADED %c %c ${gameCount} %c game for %c ${weekID} %c`;
  console.log(msg, Cls.Act, '', Cls.Val, '', Cls.Msg, '');

  // update only teamMatchResults
  const batch = writeBatch(db);
  const collectionName = `stats/${yearID}/teammatchresults`;

  allGamesStats.forEach((gameStats: IGameStats) => {
    const [homeTeamStats, awayTeamStats] = getTeamStatsFromGameStats(gameStats);
    const hTeamMatchRef = doc(db, collectionName, homeTeamStats.uID).withConverter(teamMatchResultsConverter);
    batch.set(hTeamMatchRef, { ...homeTeamStats }, { merge: true });
    const aTeamMatchRef = doc(db, collectionName, awayTeamStats.uID).withConverter(teamMatchResultsConverter);
    batch.set(aTeamMatchRef, { ...awayTeamStats }, { merge: true });
  });
  await batch.commit();

  console.log(`${Emojis.eyes} %c UPDATED %c %c ${gameCount * 2} %c teams`, Cls.Act, '', Cls.Val, '');
};

/**
 * Write an array of `GameWithScoreID` to Firestore under the `stats` collection for a given seasonID
 *
 * @param {GameWithScoreID[]} gameWithScoreIDs - Array of GameWithScoreID to write
 * @param {SeasonID} seasonID - The season ID
 * @param {VoidFunction} cb - optional void callback function to be called upon completion
 *
 * @return {Promise<void>} - A promise that resolves when the write is complete
 */
export const writeGameWithScoreIDsBySeasonID = async (
  gameWithScoreIDs: GameWithScoreByQtrID[],
  seasonID: SeasonID,
  cb?: VoidFunction
): Promise<void> => {
  const ref = doc(db, `stats/${seasonID}`);

  // console.log(`${Emojis.eyes} WRITING %c ${gameWithScoreIDs.length} %c`, Cls.Info, '');
  await setDoc(
    ref,
    { gamesWithScores: arrayUnion(...gameWithScoreIDs), modifiedOn: Date.now(), timestamp: serverTimestamp() },
    { merge: true }
  );
  const msg = `${Emojis.eyes} %c WRITTEN %c %c ${gameWithScoreIDs.length} %c %c GameWithScoreIDs %c`;
  console.log(msg, Cls.Act, '', Cls.Val, '', Cls.Info);

  cb?.();

  return;
};

export const fetchGameWithScoreIDsBySeasonID = async (seasonID: SeasonID): Promise<GameWithScoreByQtrID[]> => {
  const ref = doc(db, `stats/${seasonID}`);

  const docSnap = await getDoc(ref);
  if (docSnap.exists()) {
    const seasonData = docSnap.data();
    const gamesWithScoreIDs = seasonData.gamesWithScores as GameWithScoreByQtrID[];
    return gamesWithScoreIDs || [];
  }
  console.warn(`${Emojis.warning} No GamesWithScoreID records found for season: %c ${seasonID} %c`, Cls.Val, '');
  return [];
};
